import * as moment from 'moment';
import * as PropTypes from 'prop-types';
import * as React from 'react';
import { connect } from 'react-redux';
import { union, uniq, some } from 'lodash';
import { hideLoadingScreen } from '../../actions/app-actions';
import { backToFilters, confirmQuickCheckoutAndComplete, quickCheckout } from '../../cart/actions/cart-actions';
import { ItemType } from '../../classes/item-type';
import Modal from '../../common/modal/modal';
import ModalState from '../../common/modal/modal-states';
import { getSelectedTicketCount } from '../../helpers/price-type-helpers';
import { IAppState } from '../../reducers/index';
import { removeSeatingOptionGroup, clearAdditionalSeatRequest, selectPriceTypeTickets, selectSeatingOptionQuantity, selectSeatingOptions, toggleAisleAccess, toggleSeatSeparation, selectVenueLevel, resetVenueLevels } from '../actions/filter-actions';
import { fetchSeatMatches } from '../actions/filter-fetch';
import { clearPriceRange, selectMaxPrice, selectMinPrice } from '../actions/price-range-actions';
import { applyPromoCode, cancelPromoApply, emptyCart, removePromoCode } from '../actions/promo-actions';
import FilterPageHeaderContainer from '../components/filter-page-header-container';
import FilterResults from '../components/filter-result';
import PackagesProgressHeader from '../components/packages-progress-header';
import PerformanceInfo from '../components/performance-info';
import PerformanceList from '../components/performance-list';
import PerformanceSelector from '../components/performance-selector-menu';
import PriceRangeFilter from '../components/price-range-filter';
import PriceTypeFilterContainer from '../components/price-type-filter-container';
import PromoCodeContainer from '../../common/promo-code/promo-code';
import SeatingOptionFilter from '../components/seating-option-filter';
import VenueLevelFilter from '../components/venue-level-filter';
import { convertNumberListToString } from '../../helpers/array-helpers';
import { Level } from '../../classes/level';
import { Html } from '../../helpers/html-helpers';
import SecondaryNav from '../../menu/components/secondary-nav';


export interface IFilterState {
    showPerformanceSelector: boolean;
    showNoSeatMatchForQuickCheckoutError: ModalState;
    showOnlyADASeatsModal: ModalState;
    chooseMySeatsModal: ModalState;
    onlyGALevelsModal: ModalState;
    quickCheckoutSpecialSeatingModalState: ModalState;
    lastFocused: any;
    modalFocusableElements: any[];
};

class Filter extends React.Component<IAppState, IFilterState>{
    static contextTypes = {
        router: PropTypes.object
    }

    hasFuturePerformances: boolean; //current production has more performances in the future
    validDate: boolean; //current performance is in the future

    get promoCodeNeeded() {
        // If item isn't on sale because we need a promo code and don't have one, return true
        const { appSettingsModel, promoCodeModel } = this.props;
        return !appSettingsModel.itemOnSale && appSettingsModel.promoCodeOnly && !promoCodeModel.promoCode;
    }

    constructor(props: IAppState) {
        super(props);
        // If no app settings are passed in, let it through until something comes.
        let hasNotPassed: boolean;
        if (props.appSettingsModel.performanceDate) {
            const currPerformanceDate = moment(props.appSettingsModel.performanceDate);
            hasNotPassed = currPerformanceDate.diff(moment()) >= 0;
        } else {
            hasNotPassed = true;
        }
        this.validDate = hasNotPassed;

        this.hasFuturePerformances = some(props.performanceDatesAndTimesModel.dateTimes, dateTime => {
            return moment(dateTime).diff(moment()) >= 0;
        });

        this.state = {
            showPerformanceSelector: !hasNotPassed,
            showNoSeatMatchForQuickCheckoutError: ModalState.CLOSE,
            showOnlyADASeatsModal: props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalTitle.length === 0
                && props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalMessage.length === 0 ? ModalState.KEEP_CLOSED
                : ModalState.CLOSE,
            chooseMySeatsModal: (props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalTitle.length === 0
                && props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalMessage.length === 0) ? ModalState.KEEP_CLOSED
                : ModalState.CLOSE,
            lastFocused: null,
            onlyGALevelsModal: ModalState.CLOSE,
            quickCheckoutSpecialSeatingModalState: ModalState.CLOSE,
            modalFocusableElements: null
        };

        if (getSelectedTicketCount(props.priceTypesModel) > 0) {
            props.dispatch(fetchSeatMatches());
        }

        this.props.dispatch(hideLoadingScreen());
    }

    componentDidMount() {
        this.packageQuantityCheck(this.props);
    }

    get canProceed(): boolean {
        var selectedTicketQuantity = getSelectedTicketCount(this.props.priceTypesModel);
        return selectedTicketQuantity <= this.props.appSettingsModel.maxTicketQuantity
            && (this.props.appSettingsModel.minTicketQuantity > 0 ? (selectedTicketQuantity >= this.props.appSettingsModel.minTicketQuantity)
                : true);
    }

    get canProceedToSeatmap(): boolean {
        let selectedLevels = this.props.venueLevelModel.venueLevels.filter(lvl => lvl.selected);
        if (selectedLevels.length == 0)
            return true;

        return !(selectedLevels.every(lvl => lvl.isGaLevel)
            && (selectedLevels.filter(lvl => lvl.isGaLevel).length !== this.props.venueLevelModel.venueLevels.length));
    }

    get disableTextForQuickCheckout(): string {
        if (this.canProceed && this.props.seatMatchResultModel.loadedResults && this.props.seatMatchResultModel.numberOfTicketsAvailable > 0) {
            return "";
        }

        return "disabled";
    }

    get disableText(): string {
        if (this.canProceed && this.canProceedToSeatmap) {
            return "";
        }

        return "disabled";
    }

    packageQuantityCheck(props: IAppState) {
        var packageTicketQuantity = (window as any).packageTicketQuantity;
        const isSetQuantityPackage = props.appSettingsModel.itemType === ItemType.Flex || props.appSettingsModel.itemType === ItemType.Super;
        const quantityNotSetServerside = packageTicketQuantity === undefined || packageTicketQuantity.length === 0;
        const quantityNotSetClientside = props.priceTypesModel.priceTypes.filter(pt => pt.selectedQuantity > 0).length === 0;

        const isVariablePricingPackage = props.appSettingsModel.isVariablePriceType;
        if (isSetQuantityPackage && quantityNotSetServerside && quantityNotSetClientside && !isVariablePricingPackage) {
            this.context.router.history.push("/ticket-select");
        }
    }

    componentWillReceiveProps(props: IAppState) {
        this.packageQuantityCheck(props);

        // If no app settings are passed in, let it through until something comes.
        let hasNotPassed: boolean;
        if (props.appSettingsModel.performanceDate) {
            const currPerformanceDate = moment(props.appSettingsModel.performanceDate);
            hasNotPassed = currPerformanceDate.diff(moment()) >= 0;
        } else {
            hasNotPassed = true;
        }

        let noSeatMatchedError = ModalState.CLOSE;

        if (this.state.showNoSeatMatchForQuickCheckoutError === ModalState.KEEP_CLOSED) {
            noSeatMatchedError = ModalState.KEEP_CLOSED;
        } else if (props.seatMatchResultModel.dataHasLoaded && !props.seatMatchResultModel.requesting && props.seatMatchResultModel.numberOfTicketsAvailable === 0) {
            noSeatMatchedError = ModalState.OPEN;
        }

        let onlyADASeatsModalOpen = ModalState.CLOSE;

        if (this.state.showOnlyADASeatsModal === ModalState.KEEP_CLOSED) {
            onlyADASeatsModalOpen = ModalState.KEEP_CLOSED;
        } else if (props.seatMatchResultModel.dataHasLoaded && !props.seatMatchResultModel.requesting && props.seatMatchResultModel.onlyADASeats) {
            onlyADASeatsModalOpen = ModalState.OPEN;
        }

        if (props.seatMatchResultModel.numberOfTicketsAvailable > 0) {
            noSeatMatchedError = ModalState.CLOSE;
        }

        let quickCheckoutSpecialSeatingState = ModalState.CLOSE;

        if (this.state.quickCheckoutSpecialSeatingModalState === ModalState.KEEP_CLOSED) {
            quickCheckoutSpecialSeatingState = ModalState.KEEP_CLOSED;
        } else if (!props.seatMatchResultModel.quickCheckoutMessage || props.seatMatchResultModel.quickCheckoutMessage.length === 0) {
            quickCheckoutSpecialSeatingState = ModalState.CLOSE;
        } else {
            quickCheckoutSpecialSeatingState = ModalState.OPEN;
        }

        this.setState({
            showPerformanceSelector: !hasNotPassed,
            showNoSeatMatchForQuickCheckoutError: noSeatMatchedError,
            showOnlyADASeatsModal: onlyADASeatsModalOpen,
            chooseMySeatsModal: this.state.chooseMySeatsModal === ModalState.KEEP_CLOSED ?
                ModalState.KEEP_CLOSED
                : ModalState.CLOSE,
            lastFocused: null,
            modalFocusableElements: null,
            onlyGALevelsModal: ModalState.CLOSE,
            quickCheckoutSpecialSeatingModalState: quickCheckoutSpecialSeatingState
        });
    }

    render() {
        const { itemType } = this.props.appSettingsModel;
        const requiresMultiSelect = (itemType === ItemType.Flex && !this.props.appSettingsModel.isVariablePriceType) || itemType === ItemType.Super;
        const showProgress = itemType === ItemType.Flex || itemType === ItemType.Super;

        const showPromoCode = this.validDate && itemType !== ItemType.Flex && itemType !== ItemType.Super && this.props.appSettingsModel.enablePromoCodes;

        return (
            <div>
                <PackagesProgressHeader
                    {...this.props.appSettingsModel}
                    showComponent={showProgress}
                    selectedQuantity={(this.props.appSettingsModel.isVariablePriceType && this.props.appSettingsModel.minTicketQuantity === this.props.appSettingsModel.maxTicketQuantity) ? this.props.appSettingsModel.maxTicketQuantity
                        : getSelectedTicketCount(this.props.priceTypesModel)}
                />
                {

                    (this.props.appSettingsModel.itemType !== ItemType.Super && (this.props.appSettingsModel.performanceDateString !== null
                        || this.props.appSettingsModel.venueName.length > 0)) ?
                        <SecondaryNav performanceDate={this.props.appSettingsModel.performanceDateString} performanceTime={this.props.appSettingsModel.performanceTimeString} performanceVenue={this.props.appSettingsModel.venueName} />
                        : null
                }

                <main className="filters-container" id="main-content">

                    <FilterPageHeaderContainer {...this.props.appSettingsModel} />

                    <PerformanceInfo {...this.props.appSettingsModel}
                        performanceDateString={this.props.appSettingsModel.performanceDateString}
                        performanceTimeString={this.props.appSettingsModel.performanceTimeString}
                        onChangeDateClick={this._onChangePerformanceDateClick}
                        showComponent={itemType !== ItemType.Fixed && itemType !== ItemType.Super}
                        hideDateChange={itemType === ItemType.Flex || !this.props.appSettingsModel.enableDateChange || Object.keys(this.props.performanceDatesAndTimesModel.dateTimes).length < 1} />

                    <PerformanceList performances={this.props.appSettingsModel.packagePerformances}
                        performancesHeaderTitle={this.props.appSettingsModel.performanceListFilterTitle}
                        altText={this.props.appSettingsModel.altText}
                        showPerformancesOnLoad={this.props.appSettingsModel.showPackagePerformancesOnLoad}
                        showComponent={itemType === ItemType.Fixed || itemType === ItemType.Super}
                        performancesSectionDescription={this.props.appSettingsModel.performanceListMessage} />

                    {this.promoCodeNeeded && this.renderNoPricing()}

                    {showPromoCode && <PromoCodeContainer />}

                    {
                        /*
                         * 
                    <PromoCode {...this.props.promoCodeModel}
                        {...this.props.appSettingsModel}

                        promoCodeRedirect={this.props.appSettingsModel.currentPromoCode != null ? this.props.appSettingsModel.currentPromoCode.redirectURL : ''}
                        promoCodeTextTitle={this.props.appSettingsModel.currentPromoCode != null ? this.props.appSettingsModel.currentPromoCode.title : ''}
                        promoCodeDescription={this.props.appSettingsModel.currentPromoCode != null ? this.props.appSettingsModel.currentPromoCode.description : ''}
                        promoCodeText4={this.props.appSettingsModel.currentPromoCode != null ? this.props.appSettingsModel.currentPromoCode.text4 : ''}
                        promoCodeText5={this.props.appSettingsModel.currentPromoCode != null ? this.props.appSettingsModel.currentPromoCode.text5 : ''}
                        promoCodeText6={this.props.appSettingsModel.currentPromoCode != null ? this.props.appSettingsModel.currentPromoCode.text6 : ''}
                        onApplyPromoClick={this._onApplyPromoClick}
                        onCancelPromoClick={this._onCancelPromoClick}
                        onEmptyCartClick={this._onEmptyCartClick}
                        onRemovePromoClick={this._onRemovePromoClick}
                        showComponent={this.validDate && !requiresMultiSelect} />

                         */
                    }

                    {this.renderPriceInfo(requiresMultiSelect)}

                </main>
                {
                    !this.validDate && !this.hasFuturePerformances ?
                        <Modal modalKey="all performances passed" isError={true}
                            title={this.props.appSettingsModel.allPerformancesPassedModalTitle}
                            message={this.props.appSettingsModel.allPerformancesPassedModalMessage}
                            confirmFunction={this._performanceNotOnSaleRedirect} confirmString={this.props.appSettingsModel.modalCancelButtonText}
                            closeFunction={this._performanceNotOnSaleRedirect}
                            priority={1}
                        />
                        : null
                }

                {
                    this._quickCheckoutNoticeModal()
                }

                {
                    this._onlyADASeatsNoticeModal()
                }

                {
                    this._quickCheckoutSpecialSeatingNoticeModal()
                }

                {
                    this.validDate && this.state.showPerformanceSelector ?
                        <PerformanceSelector title={this.props.appSettingsModel.changeDateSelectionHeader} performanceDatesAndTimesModel={this.props.performanceDatesAndTimesModel} isCancelable={true} closeModalFunction={this._hidePerformanceSelector.bind(this)} appSettingsModel={this.props.appSettingsModel}></PerformanceSelector>
                        : null
                }

                {
                    this.state.chooseMySeatsModal === ModalState.OPEN ?
                        <Modal modalKey="no seats match filter proceed to seat map" isError={false} title={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalTitle} message={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalMessage}
                            confirmFunction={this._proceedToSeatmap.bind(this)}
                            confirmString={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalProceed}
                            cancelFunction={this._closeChooseMySeatsModal.bind(this)}
                            cancelString={this.props.appSettingsModel.noSeatsMatchFilterGoToSeatmapModalCancel}
                            closeFunction={this._closeChooseMySeatsModal.bind(this)}
                        />
                        : null
                }

                {
                    this.state.onlyGALevelsModal === ModalState.OPEN ?
                        <Modal modalKey={"only GA levels"} isError={false} title={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalTitle} message={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalMessage}
                            confirmFunction={this._proceedToSeatmap.bind(this)}
                            confirmString={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalProceed}
                            cancelFunction={this._closeOnlyGALevelsModal.bind(this)}
                            closeFunction={this._closeOnlyGALevelsModal.bind(this)}
                            cancelString={this.props.appSettingsModel.onlyGALevelsSelectedGoToSeatmapModalCancel} />
                        : null
                }

                {
                    this.props.appSettingsModel.maxTicketQuantity == 0
                        ?
                        <Modal modalKey="ticket quantity zero"
                            isError={true}
                            title={this.props.appSettingsModel.ticketQuantityZeroModalTitle}
                            message={this.props.appSettingsModel.ticketQuantityZeroModalMessage}
                            confirmFunction={this._ticketQuantityZeroRedirect.bind(this)}
                            confirmString={this.props.appSettingsModel.ticketQuantityZeroRedirectButtonText}
                            closeFunction={this._ticketQuantityZeroRedirect.bind(this)}
                        />
                        : null
                }

                {this.renderFooter()}
            </div>

        );
    }

    renderNoPricing = () => {
        return (
            <section className="filters-section">
                {Html.renderDiv(this.props.appSettingsModel.filterPageNoPricingMessage)}
            </section>
        )
    }

    renderPriceInfo = (requiresMultiSelect: boolean) => {
        if (this.promoCodeNeeded) {
            return;
        }

        const { itemType } = this.props.appSettingsModel;

        return (
            <>
                <PriceTypeFilterContainer
                    priceTypes={this.props.priceTypesModel.priceTypes}
                    onChangePriceTypeQuantity={this._onChangePriceTypeQuantity}
                    appSettingsModel={this.props.appSettingsModel}
                    showComponent={this.validDate && !requiresMultiSelect} />

                <PriceRangeFilter {...this.props.priceRangeModel}
                    {...this.props.appSettingsModel}
                    onSelectMinPrice={this._onSelectMinPrice}
                    onSelectMaxPrice={this._onSelectMaxPrice}
                    onPriceRangeClear={this._onClearPriceRange}
                    startingMaxPrice={this.props.priceRangeModel.maxPrice}
                    startingMinPrice={this.props.priceRangeModel.minPrice}
                    showComponent={this.validDate && (this.props.priceTypesModel.priceTypes.length > 1
                        || this.props.priceRangeModel.lowerRangeLimit !== this.props.priceRangeModel.upperRangeLimit)} />

                <VenueLevelFilter levels={this.props.venueLevelModel.venueLevels}
                    {...this.props.appSettingsModel}
                    onResetLevels={this._onResetVenueLevel}
                    onSelectLevel={this._onSelectVenueLevel}
                    dispatch={this.props.dispatch}
                    selectedPriceTypeIdsString={convertNumberListToString(this.props.priceTypesModel.priceTypes.filter(pt => pt.selectedQuantity > 0).map(pt => pt.priceTypeId))}
                    showComponent={this.validDate}
                    selectedLevelString={this.props.venueLevelModel.venueLevels.filter(level => {
                        return level.selected;
                    }).map(level => {
                        return level.levelNumber;
                    }).toString()}
                    isCheckbox={true}
                />

                <SeatingOptionFilter onToggleSeatSeparation={this._onToggleSeatSeparation}
                    onToggleAisleAccess={this._onToggleAisleAccess}
                    {...this.props.additionalSeatingOptionModel}
                    allowAisleAccessSelected={this.props.additionalSeatingOptionModel.allowAisleAccessSelected}
                    removeSeatingOptionGroup={this._onRemoveSeatingOptionGroup}
                    selectedTicketQuantity={getSelectedTicketCount(this.props.priceTypesModel)}
                    onAdditionalSeatRequestClear={this._onAdditionalSeatRequestClear}
                    onSelectSeatingOption={this._onSelectSeatingOption}
                    onSelectSeatingOptionQuantity={this._onSelectSeatingOptionQuantity}
                    appSettings={this.props.appSettingsModel}
                    showComponent={this.validDate}
                    showAdditionalNotes={this.props.appSettingsModel.renderAdditionalNotes}
                />
            </>
        )
    }

    renderFooter = () => {
        if (this.promoCodeNeeded) {
            return;
        }

        return (
            <footer className="footer-wrapper">
                <FilterResults loadedResults={this.props.seatMatchResultModel.loadedResults}
                    numberOfTicketOptions={this.props.seatMatchResultModel.numberOfTicketOptions}
                    requesting={this.props.seatMatchResultModel.requesting}
                    filterSearchingToast={this.props.appSettingsModel.filterSearchingToast} />

                <div className="footer-container">
                    {
                        !this.props.appSettingsModel.isGeneralAdmissionVenue ?
                            <button type="button" onClick={() => this._onProceedClick()} className={"footer-link " + this.disableText}><span>{this.props.appSettingsModel.filterProceedButton}</span></button>
                            : null
                    }
                    <button type="button" disabled={this.disableTextForQuickCheckout.length > 0} className={"footer-link " + this.disableTextForQuickCheckout} onClick={this._onQuickCheckoutClick.bind(this)}>{this.props.appSettingsModel.filterQuickCheckoutButton}</button>
                </div>
            </footer>
        )
    }

    _closeChooseMySeatsModal() {
        this.setState({
            chooseMySeatsModal: ModalState.CLOSE
        });
    }

    _closeOnlyGALevelsModal() {
        this.setState({
            onlyGALevelsModal: ModalState.CLOSE
        });
    }

    _onProceedClick() {
        // If there are 0 seats matched, show a modal letting them know.
        if (this.props.seatMatchResultModel.numberOfTicketsAvailable === 0
            && this.state.chooseMySeatsModal !== ModalState.KEEP_CLOSED) {
            this.setState({
                chooseMySeatsModal: ModalState.OPEN
            });
        } else if (!this.canProceedToSeatmap) {
            this.setState({
                onlyGALevelsModal: ModalState.OPEN
            });
        } else {
            this._proceedToSeatmap();
        }
    }

    _proceedToSeatmap() {
        this.context.router.history.push(`/seatmap${this.props.location.search}`);
    }

    _closeSpecialSeatingModalAndBackToFilters() {
        this.props.dispatch(backToFilters());
        this._closeQuickCheckoutSpecialSeatingNoticeModal();
    }

    _closeQuickCheckoutSpecialSeatingNoticeModal() {
        this.setState({
            quickCheckoutSpecialSeatingModalState: ModalState.CLOSE
        });
    }

    _quickCheckoutSpecialSeatingNoticeModal(): JSX.Element {
        switch (this.state.quickCheckoutSpecialSeatingModalState) {
            case ModalState.OPEN:
            case ModalState.KEEP_OPEN:
                return (
                    <Modal modalKey="missing attestation" title={this.props.appSettingsModel.missingAttestationErrorMessageHeader}
                        message={this.props.seatMatchResultModel.quickCheckoutMessage}
                        cancelString={this.props.appSettingsModel.modalCancelButtonText}
                        cancelFunction={this._closeSpecialSeatingModalAndBackToFilters.bind(this)}
                        confirmFunction={this._quickCheckout.bind(this)}
                        confirmString={this.props.appSettingsModel.modalConfirmButtonText}
                        closeFunction={this._closeSpecialSeatingModalAndBackToFilters.bind(this)}
                    />
                );
            default:
                return null;
        }
    }

    _quickCheckoutNoticeModal(): JSX.Element {
        switch (this.state.showNoSeatMatchForQuickCheckoutError) {
            case ModalState.OPEN:
            case ModalState.KEEP_OPEN:
                return (
                    <Modal modalKey="no seats match quick checkout"
                        title={this.props.appSettingsModel.noSeatsMatchFilterQuickCheckoutModalTitle}
                        message={this.props.appSettingsModel.noSeatsMatchFilterQuickCheckoutModalMessage}
                        confirmString={this.props.appSettingsModel.modalConfirmButtonText}
                        confirmFunction={this._closeQuickCheckoutModal}
                        closeFunction={this._closeQuickCheckoutModal}
                    />
                );
            default:
                return null;
        };
    }

    _onlyADASeatsNoticeModal(): JSX.Element {
        switch (this.state.showOnlyADASeatsModal) {
            case ModalState.OPEN:
            case ModalState.KEEP_OPEN:
                return (
                    <Modal modalKey="only ADA seats match filters"
                        title={this.props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalTitle}
                        message={this.props.appSettingsModel.onlyADASeatsMatchFilterGoToSeatmapModalMessage}
                        confirmString={this.props.appSettingsModel.modalConfirmButtonText}
                        confirmFunction={this._closeADAOnlyModal.bind(this)}
                        closeFunction={this._closeADAOnlyModal.bind(this)}
                    />
                );
            default:
                return null;
        };
    }

    _onChangePerformanceDateClick = () => {
        this._showPerformanceSelector();
    };

    _showPerformanceSelector() {
        let lastFocused = document.activeElement;

        this.setState({
            showPerformanceSelector: true,
            lastFocused: lastFocused,
        });
        (document.activeElement as any).blur();
    }

    _hidePerformanceSelector() {
        this.setState({
            showPerformanceSelector: false,
        });

        this.state.lastFocused.focus();
    }

    _performanceNotOnSaleRedirect = () => {
        window.location.href = this.props.appSettingsModel.performanceNotOnSaleRedirectUrl;
    }

    _ticketQuantityZeroRedirect() {
        window.location.href = this.props.appSettingsModel.ticketQuantityZeroRedirectUrl;
    }

    _onSelectVenueLevel = (level: Level) => {
        this.props.dispatch(selectVenueLevel(level.levelNumber));
    };

    _onResetVenueLevel() {
        this.props.dispatch(resetVenueLevels());
    }

    _onSelectSeatingOptionQuantity = (seatingOptionGroupId: string, quantity: number) => {
        this.props.dispatch(selectSeatingOptionQuantity(seatingOptionGroupId, quantity));
    }

    _onSelectSeatingOption = (seatingOptionGroupId: string, seatingOptions: Array<any>, seatingOptionToggle: boolean) => {
        this.props.dispatch(selectSeatingOptions(seatingOptionGroupId, seatingOptions, seatingOptionToggle));
    };

    _onToggleSeatSeparation = (evt) => {
        this.props.dispatch(toggleSeatSeparation(evt.target.checked));
    };

    _onChangePriceTypeQuantity = (quantity: number, priceTypeId: number) => {
        this.props.dispatch(selectPriceTypeTickets(priceTypeId, quantity, false, [], true));
    };

    _onApplyPromoClick = (promoCode) => {
        this.props.dispatch(applyPromoCode(promoCode));
    };

    _onCancelPromoClick = () => {
        this.props.dispatch(cancelPromoApply());
    };

    _onRemovePromoClick = () => {
        this.props.dispatch(removePromoCode());
    };

    _onEmptyCartClick = (promoCode) => {
        this.props.dispatch(emptyCart(promoCode));
    };

    _onClearPriceRange = (minPrice, maxPrice) => {
        this.props.dispatch(clearPriceRange(minPrice, maxPrice))
    };

    _onSelectMinPrice = (minPrice: number) => {
        this.props.dispatch(selectMinPrice(minPrice));
    };

    _onSelectMaxPrice = (maxPrice: number) => {
        this.props.dispatch(selectMaxPrice(maxPrice))
    };

    _onToggleAisleAccess = (evt) => {
        this.props.dispatch(toggleAisleAccess(evt.target.checked));
    }

    _onRemoveSeatingOptionGroup = (seatingOptionGroupId: string) => {
        var companionSeatingOptions = this.props.additionalSeatingOptionModel.additionalSeatingOptions.filter(seatingOption => seatingOption.unlockSeatingOptionGroupId !== null && seatingOption.unlockSeatingOptionGroupId === seatingOptionGroupId);

        companionSeatingOptions.forEach(companionSeating => {
            this.props.dispatch(removeSeatingOptionGroup(companionSeating.id));
        });

        this.props.dispatch(removeSeatingOptionGroup(seatingOptionGroupId));
    }

    _onAdditionalSeatRequestClear = () => {
        this.props.dispatch(clearAdditionalSeatRequest());
    };

    _onQuickCheckoutClick() {
        this.props.dispatch(confirmQuickCheckoutAndComplete());
    }

    _quickCheckout() {
        this._closeQuickCheckoutSpecialSeatingNoticeModal();
        this.props.dispatch(quickCheckout());
    }

    _closeQuickCheckoutModal = () => {
        this.setState({
            showNoSeatMatchForQuickCheckoutError: ModalState.KEEP_CLOSED
        });
    }

    _closeADAOnlyModal() {
        this.setState({
            showOnlyADASeatsModal: ModalState.KEEP_CLOSED
        });
    }
}

//export default Filter;
export default connect((state) => state)(Filter);